import type { ReactNode } from 'react';
import type { OptionBuyingStrategy } from '@einhaus/project314-types';
import Disclosure from '@/components/UI/Disclosure';
import type { StrategySubscriptionRow } from './strategySubscription';
import {
    ActivationCell,
    DeploymentCell,
    PositionSizingCell,
    StrategyIdentityCell,
    SubscriptionActionsCell,
    TradingProfileCell
} from './StrategySubscriptionCells';
import StrategySubscriptionDetails from './StrategySubscriptionDetails';

interface StrategySubscriptionMobileListProps {
    rows: StrategySubscriptionRow[];
    /** Arrangement of the cards; defaults to a single stacked column. */
    className?: string;
    optionBuyingStrategiesById: ReadonlyMap<number, OptionBuyingStrategy>;
    onToggleActive: (row: StrategySubscriptionRow) => void;
    onConfigure: (row: StrategySubscriptionRow) => void;
    onManageScorecards: (row: StrategySubscriptionRow) => void;
    /** Omitted where subscriptions cannot be removed. */
    onRemove?: (row: StrategySubscriptionRow) => void;
    /** Card heading; defaults to the strategy, and lists grouped by strategy name the portfolio instead. */
    renderIdentity?: (row: StrategySubscriptionRow) => ReactNode;
}

const renderStrategyIdentity = (row: StrategySubscriptionRow) => <StrategyIdentityCell row={row} />;

/**
 * Phone layout for the subscriptions table. A seven-column table only survives narrow screens as a
 * horizontal scroll, so each subscription becomes a card carrying the same cells in reading order.
 */
const StrategySubscriptionMobileList = ({
    rows,
    className = 'space-y-3',
    optionBuyingStrategiesById,
    onToggleActive,
    onConfigure,
    onManageScorecards,
    onRemove,
    renderIdentity = renderStrategyIdentity
}: StrategySubscriptionMobileListProps) => (
    <ul className={className}>
        {rows.map((row) => (
            <li className="rounded-lg border border-zinc-800 bg-[#222222] p-3" key={row.key}>
                <div className="flex items-start justify-between gap-3">
                    {renderIdentity(row)}
                    <ActivationCell row={row} onToggle={onToggleActive} />
                </div>

                <dl className="mt-3 grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-2">
                    <dt className="text-xs uppercase tracking-wider text-zinc-500">Profile</dt>
                    <dd>
                        <TradingProfileCell row={row} onConfigure={onConfigure} />
                    </dd>
                    <dt className="text-xs uppercase tracking-wider text-zinc-500">Sizing</dt>
                    <dd>
                        <PositionSizingCell row={row} />
                    </dd>
                    <dt className="text-xs uppercase tracking-wider text-zinc-500">Entries</dt>
                    <dd>
                        <DeploymentCell row={row} />
                    </dd>
                </dl>

                <div className="mt-3 flex justify-end">
                    <SubscriptionActionsCell
                        row={row}
                        onConfigure={onConfigure}
                        onManageScorecards={onManageScorecards}
                        onRemove={onRemove}
                    />
                </div>

                <Disclosure className="mt-3" title="Details" variant="plain">
                    <StrategySubscriptionDetails
                        openPositionCount={row.openPositionCount}
                        optionBuyingStrategiesById={optionBuyingStrategiesById}
                        readiness={row.readiness}
                        sizing={row.sizing}
                        strategy={row.strategy}
                        tradingProfileName={row.tradingProfileName}
                        onConfigure={() => onConfigure(row)}
                    />
                </Disclosure>
            </li>
        ))}
    </ul>
);

export default StrategySubscriptionMobileList;
